import React from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, FolderOpen } from "lucide-react";
import { PageShell } from "../../components/PageShell";
import { Breadcrumb } from "../../components/Breadcrumb";
import { useDashTheme } from "../../hooks/useDashTheme";
import { Button } from "../../components/primitives";
import { EmptyState } from "../../components/DataTable";
import { DashAntd } from "../../components/DashAntd";
import * as api from "../../components/drilldown/drilldownApi";
import { useDrilldownData } from "../../components/drilldown/useDrilldownData";
import {
  useClassicFilters, classicPath, projectPath, projectMemberPath, personPath,
} from "../../components/drilldown/drilldownRoutes";
import { ContributorList, MemberInProject } from "../../components/drilldown/MemberInProject";
import { SourceGap, fmtMins } from "../../components/drilldown/sourceUi";
import { Section, Rail, ReadCard, MetricHeader, Crumbs, TakeawayCard, KV, ScopeBar, ColLabel } from "./shared";
import { isGraphicalDrill } from "../graphical/drill/DrillFrame";
import { GraphicalProject } from "../graphical/drill/GraphicalProject";

/**
 * ProjectDrilldown — the project half of layers 2 and 3, as pages.
 *
 *   view="digest"    the project's hours, its key takeaways, and who worked on it
 *   view="takeaway"  ONE takeaway with the figures it was computed from — these
 *                    takeaways are derived in code (see drilldownApi.projectTakeaways),
 *                    so "the detail behind it" is honestly the arithmetic, not a
 *                    second opinion from a model
 *   view="member"    what one contributor did ON THIS PROJECT
 */
export function ProjectDrilldown({ view = "digest" }) {
  const t = useDashTheme();
  const navigate = useNavigate();
  const { projectId, index, userId } = useParams();
  const [params] = useSearchParams();
  const { range, member } = useClassicFilters();

  const nameHint = params.get("name") || "";
  /* `sit` is the graphical branch's sitting selection — view-local, so it
     must never be forwarded into other links. Classic never sets it, so
     stripping it is a no-op there. */
  const qs = React.useMemo(() => {
    const p = new URLSearchParams(params);
    p.delete("sit");
    return p.toString();
  }, [params]);
  const link = (path) => (qs ? `${path}?${qs}` : path);

  const { related, read, readErr, loading, error } = useDrilldownData({
    kind: "project", id: projectId, name: nameHint, range,
  });

  const name = nameHint || "Project";
  const totals = related?.projectTotals || {};
  const contributors = related?.memberHours || [];
  const takeaways = React.useMemo(() => (related ? api.projectTakeaways(related) : []), [related]);

  const focusIndex = view === "takeaway" ? Number(index) : null;
  const takeaway = focusIndex != null ? takeaways[focusIndex] : null;
  const themeTitle = params.get("theme") || takeaway?.title || "";
  const contributor = userId ? contributors.find((m) => String(m.userId) === String(userId)) : null;

  const backToDigest = React.useCallback(
    () => navigate(projectPath(projectId, range, member, name)),
    [navigate, projectId, range, member, name]
  );

  React.useEffect(() => {
    /* Graphical frames own Escape themselves (clear the sitting first,
       then unwind a level) — classic keeps this exact behaviour. */
    if (view === "digest" || isGraphicalDrill()) return undefined;
    const onKey = (e) => { if (e.key === "Escape") backToDigest(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [view, backToDigest]);

  const crumbs = [
    { label: "Dashboard", to: classicPath(range, member) },
    view === "digest" ? { label: name } : { label: name, to: link(projectPath(projectId)) },
    view === "takeaway"
      ? { label: themeTitle || takeaway?.title || "This piece of work" }
      : null,
    view === "member"
      ? { label: contributor ? api.memberName(contributor) : "Contributor" }
      : null,
  ].filter(Boolean);

  const periodLabel = `${new Date(range.start).toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${new Date(range.end).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  const trackedMinutes = Math.round((Number(totals.totalHours) || 0) * 60);
  const idleMinutes = Number(totals.totalIdleTimeMinutes) || 0;
  const activeMinutes = Math.max(0, trackedMinutes - idleMinutes);

  if (isGraphicalDrill()) {
    const layer = view === "digest" ? 1 : 2;
    return (
      <GraphicalProject
        t={t}
        view={view}
        name={name}
        periodLabel={periodLabel}
        loading={loading}
        error={error}
        themeTitle={themeTitle}
        contributors={contributors}
        contributor={contributor}
        trackedMinutes={trackedMinutes}
        activeMinutes={activeMinutes}
        idleMinutes={idleMinutes}
        crumbs={crumbs}
        backTo={view === "digest" ? classicPath(range, member) : projectPath(projectId, range, member, name)}
        backLabel={layer === 1 ? "Dashboard" : "Back"}
        subject={view === "member" ? [name, contributor ? api.memberName(contributor) : "Contributor"] : [name]}
        excludes={["every other project", "hours recorded without a project selection"]}
        openTheme={(th) => {
          const p = new URLSearchParams(qs);
          p.set("theme", th.title);
          p.set("name", name);
          navigate(`${projectPath(projectId)}/takeaway/0?${p.toString()}`);
        }}
        openMember={(m) => navigate(projectMemberPath(projectId, m.userId, range, member, name))}
        openPerson={(id, pn) => navigate(personPath(id, range, member, pn))}
        onBack={backToDigest}
        projectId={projectId}
        range={range}
      />
    );
  }

  const evidence = [
    ["Tracked", fmtMins(trackedMinutes)],
    ["Active", totals.totalActiveTimeFormatted || "0h 0m"],
    ["Idle", totals.totalIdleTimeFormatted || "0h 0m"],
    ["Sessions", totals.sources?.sessionsCoverAllTime === false
      ? `${totals.totalSessions || 0} (app only)`
      : (totals.totalSessions || 0)],
    ["Contributors", totals.activeMembers ?? contributors.length],
  ];

  return (
    <PageShell
      title={name}
      subtitle={periodLabel}
      icon={<FolderOpen size={16} />}
      actions={
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<ArrowLeft size={14} />}
          onClick={() => navigate(view === "digest" ? classicPath(range, member) : projectPath(projectId, range, member, name))}
        >
          {view === "digest" ? "Dashboard" : "Back"}
        </Button>
      }
    >
      <DashAntd>
        <Crumbs><Breadcrumb items={crumbs} /></Crumbs>

        {error ? (
          <EmptyState icon={<FolderOpen size={22} />} title="Couldn’t load this project" description={error} />
        ) : view === "member" ? (
          /* ── LAYER 3 — one contributor, on this project ──────────────────── */
          contributor ? (
            <MemberInProject
              member={contributor}
              project={{ id: projectId, name }}
              range={range}
              onBack={backToDigest}
              /* project → contributor → that person's OWN drill-down. The
                 drawer could hop without closing; as pages it is a link, so
                 the trail behind it stays walkable. */
              onOpenPerson={(pn) => navigate(personPath(pn.id, range, member, pn.name))}
            />
          ) : loading ? null : (
            <EmptyState
              icon={<FolderOpen size={22} />}
              title="That contributor has no time on this project"
              description="They may have dropped out of the window after the date range changed."
            />
          )
        ) : view === "takeaway" ? (
          /* ── LAYER 3 — one takeaway, and the arithmetic behind it ────────── */
          takeaway ? (
            <div style={{ display: "flex", flexDirection: "column", gap: t.s[5], maxWidth: 760 }}>
              <TakeawayCard t={t} takeaway={takeaway} active />
              <Section t={t} title="The figures behind it" hint="every takeaway is computed from these">
                <KV t={t} items={evidence} />
              </Section>
              <Section t={t} title="Who worked on it">
                <ContributorList
                  related={related}
                  onPick={(m) => navigate(projectMemberPath(projectId, m.userId, range, member, name))}
                />
              </Section>
            </div>
          ) : loading ? null : (
            <EmptyState
              icon={<FolderOpen size={22} />}
              title="That takeaway is no longer in this window"
              description="Takeaways are computed from the selected range; changing it changes which ones exist."
            />
          )
        ) : (
          /* ── LAYER 2 — the graphical digest + key takeaways ──────────────── */
          <div className="tg-drill-grid" style={{ display: "grid", gap: t.s[5], alignItems: "start" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: t.s[5], minWidth: 0 }}>
              <ColLabel t={t}>The picture</ColLabel>
              <ScopeBar
                t={t}
                subject={[name]}
                excludes={["every other project", "hours recorded without a project selection"]}
              />
              <MetricHeader
                t={t}
                label={`Tracked · ${periodLabel}`}
                value={loading ? null : fmtMins(trackedMinutes)}
                activeMinutes={activeMinutes}
                idleMinutes={idleMinutes}
              />

              {takeaways.length > 0 && (
                <Section t={t} title="Key takeaways" hint="click one for the detail behind it">
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {takeaways.map((k, i) => (
                      <TakeawayCard
                        key={`${k.title}-${i}`}
                        t={t}
                        takeaway={k}
                        onClick={() => navigate(link(`${projectPath(projectId)}/takeaway/${i}`))}
                      />
                    ))}
                  </div>
                </Section>
              )}

              <Section t={t} title="Who worked on it" hint="click a contributor for their work on this project">
                <ContributorList
                  related={related}
                  onPick={(m) => navigate(projectMemberPath(projectId, m.userId, range, member, name))}
                />
              </Section>
            </div>

            <Rail t={t}>
              <ReadCard t={t} read={read} readErr={readErr} />
              <Section t={t} title="Totals">
                <KV t={t} items={evidence} />
              </Section>
              {totals.sources?.sessionsCoverAllTime === false && (
                <SourceGap>
                  Session totals cover only {totals.sources.app?.formatted || "0h 0m"} of this window. The remaining
                  {" "}{totals.sources.agent?.formatted} is real tracked time with no session detail attached.
                </SourceGap>
              )}
            </Rail>
          </div>
        )}
      </DashAntd>
    </PageShell>
  );
}

export default ProjectDrilldown;
